import { Row } from "@tanstack/react-table";
import { CheckSquare, Delete, Link } from "lucide-react";
import React from "react";

import { getBadgeVariant } from "../../Products/product-detail-page/sub-product-items/table/ItemTable";

import { AlertDialogWithTextArea } from "@/components/alerts/AlertDialogWithTextArea";
import AvatarText from "@/components/reusables/AvatarText";
import { CustomDropdown } from "@/components/reusables/custom-action-button";
import { CustomTable } from "@/components/reusables/data-table/CustomTable";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { SELECTOR_ID } from "@/constants/global-constants";
import { ToolsConstants, ToolsKeys } from "@/constants/tools.constant";
import useGetFilter from "@/hooks/useGetFilter";
import { ResponsePagination } from "@/hooks/usePagination";
import useQueryID from "@/hooks/useQueryId";
import { GlobalRowDataTypes } from "@/interfaces/global-table.interface";
import { ExtendedColumnDef } from "@/interfaces/globals";
import {
  IProductRequest,
  IProductRequestDetail,
  ProductRequestStatus,
  TBase,
} from "@/interfaces/tools.interface";
import { cn, formatToCapitalizedWords, renderFullName } from "@/lib/utils";
import { useAlterProductRequestStatusMutation } from "@/redux/api/toolsQuery";
import { customToast } from "@/utils/custom-toast";
import { dateFormat } from "@/utils/helper-functions/date-time.helper";
import { validateResponse } from "@/utils/helper-functions/form.helper";

interface ProductRequestTableProps {
  data: IProductRequest[];
  pagination: ResponsePagination;
  toggleAssignModal: React.Dispatch<
    React.SetStateAction<TBase<IProductRequestDetail[]> | null>
  >;
  onRowClick: React.Dispatch<
    React.SetStateAction<TBase<IProductRequest> | null>
  >;
}

type TAcceptRejectDialog = {
  isOpen: boolean;
  id: string;
  requestBy: string;
} | null;

const ProductRequestTable = ({
  data,
  pagination,
  toggleAssignModal,
  onRowClick,
}: ProductRequestTableProps): JSX.Element => {
  const { projectList } = useGetFilter(["project"]);
  const { set } = useQueryID(ToolsKeys.PRODUCT_REQUEST_ID);
  const [rejectDialog, setRejectDialog] =
    React.useState<TAcceptRejectDialog>(null);
  const [acceptDialog, setAcceptDialog] =
    React.useState<TAcceptRejectDialog>(null);

  const [alterProductRequestStatus] = useAlterProductRequestStatusMutation();

  const { TITLE } = ToolsConstants.REQUEST.PRODUCT_REQUEST;
  const getActionOptions = (
    row: Row<IProductRequest>,
  ): ActionButtonDataTypes[] => {
    if (row.original.status === ProductRequestStatus.ACCEPTED) {
      return [
        {
          icon: Link,
          name: "Assign to project",
          onclick: (): void => {
            set(row.original?.id);
            toggleAssignModal({
              isOpen: true,
              data: row.original?.items || [],
            });
          },
        },
      ];
    }

    return [
      {
        icon: CheckSquare,
        name: "Accept Request",
        onclick: (): void => {
          setAcceptDialog({
            isOpen: true,
            id: row.original.id,
            requestBy: renderFullName({
              params: row.original.requestedBy,
            }),
          });
        },
      },
      {
        icon: Delete,
        name: "Reject Request",
        isTrash: true,
        onclick: (): void => {
          setRejectDialog({
            isOpen: true,
            id: row.original.id,
            requestBy: renderFullName({
              params: row.original.requestedBy,
            }),
          });
        },
      },
    ];
  };

  const columns: ExtendedColumnDef<IProductRequest>[] = [
    {
      map: {
        key: "id",
        valueFrom: "id",
      },
      id: SELECTOR_ID,
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label='Select all'
          className={cn(
            "hidden group-hover/header:block",
            table.getIsSomePageRowsSelected() && "block",
            table.getIsAllPageRowsSelected() && "!block",
          )}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label='Select row'
          onClick={(e) => e.stopPropagation()}
        />
      ),

      size: 12,
      enableSorting: false,
      enableHiding: false,
    },
    {
      map: {
        key: "Project",
        dataTransformer: (data) => data?.project?.name || "-",
      },
      accessorKey: "Project",
      header: "Project",
      cell: ({ row }) => row.original?.project?.name || "-",
      meta: {
        minWidth: 200,
        showRightBorder: true,
      },
    },
    {
      map: {
        key: "Status",
        dataTransformer: (data) =>
          formatToCapitalizedWords(data?.status || "-"),
      },
      accessorKey: "Status",
      header: "Status",
      cell: ({ row }): JSX.Element => {
        return (
          <Badge variant={getBadgeVariant(row.original?.status)}>
            {formatToCapitalizedWords(row.original?.status)}
          </Badge>
        );
      },
      meta: {
        showRightBorder: true,
        minWidth: 100,
      },
    },
    {
      map: {
        key: "Requested By",
        dataTransformer: (data) =>
          renderFullName({
            params: data.requestedBy,
          }),
      },
      accessorKey: "requested By",
      header: "Requested By",
      cell: ({ row }): JSX.Element => {
        return (
          <AvatarText
            src={row?.original?.requestedBy?.image}
            name={`${renderFullName({
              params: row.original.requestedBy,
            })}`}
            title={`${renderFullName({
              params: row.original.requestedBy,
            })}`}
          />
        );
      },
      meta: {
        minWidth: 200,
        showRightBorder: true,
      },
    },
    {
      map: {
        key: "Requested At",
        dataTransformer: (data) => dateFormat(data.createdAt, "dd/MM/yyyy"),
      },
      accessorKey: "requested At",
      header: "Requested At",
      cell: ({ row }) => dateFormat(row.original.createdAt, "dd/MM/yyyy"),
      meta: {
        minWidth: 150,
        showRightBorder: true,
      },
    },
    {
      map: {
        key: "Remarks",
        dataTransformer: (data) => data.remarks || "-",
      },
      accessorKey: "remarks",
      header: "Remarks",
      cell: ({ row }) => row.original?.remarks,
      meta: {
        minWidth: 200,
      },
    },
    {
      id: "actions",
      cell: ({ row }): React.JSX.Element => (
        <CustomDropdown data={getActionOptions(row)} />
      ),
      meta: {
        classNameRow: "w-14",
      },
    },
  ];

  const handleRequest = async (
    value: string,
    id: string,
    status: ProductRequestStatus,
    onSuccess: () => void,
  ): Promise<void> => {
    const response = await alterProductRequestStatus({
      id,
      body: { status, remarks: value },
    });

    validateResponse({
      response,
      onSuccess,
    });
  };

  return (
    <div>
      <CustomTable
        title={TITLE}
        data={data}
        columns={columns}
        empty={{}}
        hide={{
          filters: true,
          functions: true,
        }}
        functions={{
          search: TITLE,
          isExport: true,
          columnToggle: true,
          pagination,
          RightClickData: (
            row: GlobalRowDataTypes<IProductRequest>,
          ): ActionButtonDataTypes[] =>
            getActionOptions(row as Row<IProductRequest>),
          onRowClick: (id) => {
            const selectedRow = data.find((item) => item.id === id);
            onRowClick({
              data: selectedRow as IProductRequest,
              isOpen: true,
            });
          },
        }}
        filters={{
          dateRange: true,
          projectId: { label: "Project", options: projectList },
        }}
      />
      {acceptDialog?.isOpen && (
        <AlertDialogWithTextArea
          isOpen={acceptDialog.isOpen}
          toggleDialog={() => setAcceptDialog(null)}
          label='Remarks'
          placeholder='Enter remarks'
          onConfirm={async (value) => {
            await handleRequest(
              value,
              acceptDialog?.id,
              ProductRequestStatus.ACCEPTED,
              () => {
                customToast.success(
                  ToolsConstants.REQUEST.PRODUCT_REQUEST.ACCEPTED_SUCCESS,
                );
                setAcceptDialog(null);
              },
            );
          }}
          subtitle={`Are you sure you want to accept the request by ${acceptDialog.requestBy}?`}
        />
      )}
      {rejectDialog?.isOpen && (
        <AlertDialogWithTextArea
          isOpen={rejectDialog.isOpen}
          toggleDialog={() => setRejectDialog(null)}
          label='Reason'
          placeholder='Enter reason for request cancellation'
          onConfirm={async (value) => {
            await handleRequest(
              value,
              rejectDialog?.id,
              ProductRequestStatus.REJECTED,
              () => {
                customToast.success(
                  ToolsConstants.REQUEST.PRODUCT_REQUEST.REJECTED_SUCCESS,
                );
                setRejectDialog(null);
              },
            );
          }}
          confirmBtnLabel='Yes, Reject'
          subtitle={`Are you sure you want to reject the request by ${rejectDialog.requestBy}?`}
        />
      )}
    </div>
  );
};

export default ProductRequestTable;
